/**
 * A DEALERSHIP's asset type — the market it sells into.
 *
 * Distinct from every other asset-type axis in the codebase, and they are easy
 * to confuse:
 *
 *   dealership.asset_type       auto | marine   what the DEALERSHIP sells
 *   master_services.asset_type  auto | marine   the market a SERVICE sells into
 *   vehicle.asset_type          car  | boat     the STRUCTURE of an asset
 *   installer_location.marine_enabled  tinyint  whether a SHOP can do marine
 *
 * This module answers only the first. A dealership is strictly one or the
 * other; there is no "both" and no "other".
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/dealerAssetType.test.mjs` under Node's ESM syntax detection.
 * `marineCapability.js` wraps this and supplies the cached objects, because it
 * imports ScopeService (and therefore HTTPService) and cannot be harnessed.
 * Do not add imports.
 */

export const DEALER_ASSET_TYPES = { AUTO: 'auto', MARINE: 'marine' };

/** localStorage mixes string and number ids across caches. Compare as strings. */
const sameId = (a, b) =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

/**
 * A MySQL ENUM, so the value is a string — NOT a tinyint. Anything unrecognised
 * (absent column, a pre-migration row, a typo) is 'auto': the safe default,
 * because an automotive dealership is what every dealership was before this
 * feature and the wizard behaves exactly as it does today.
 */
const normalise = (raw) =>
  String(raw ?? '').trim().toLowerCase() === DEALER_ASSET_TYPES.MARINE
    ? DEALER_ASSET_TYPES.MARINE
    : DEALER_ASSET_TYPES.AUTO;

/**
 * Resolve one dealership's asset type.
 *
 * `dealerId` FIRST, companyAPI second. A dealer-group user can switch the
 * active dealership via CompanyLocationSelector while companyAPI still holds
 * `dealerships[0]`, so reading companyAPI alone would answer for the wrong
 * dealership. Callers pass the same id they use for the specialist lookup
 * (`effectiveDealerId`).
 *
 * A null/absent `dealerId` means "All Dealerships", which falls back to
 * companyAPI — i.e. `dealerships[0]`. A group holding BOTH auto and marine
 * dealerships therefore inherits the first one's type under "All Dealerships".
 * That is deliberate and matches what `effectiveDealerId` already does for
 * specialist discovery; diverging here would be worse than the inconsistency.
 *
 * @param {string|number|null} dealerId
 * @param {{company: object|null, dealerGroup: object|null}} [caches]
 * @returns {'auto'|'marine'}
 */
export function resolveDealerAssetType(dealerId, caches) {
  const { company, dealerGroup } = caches || {};

  const dealerships = Array.isArray(dealerGroup?.dealerships) ? dealerGroup.dealerships : [];
  const match = dealerships.find((d) => sameId(d?.dealer_id, dealerId));
  if (match) return normalise(match.asset_type);

  return normalise(company?.asset_type);
}

/** Convenience predicate over `resolveDealerAssetType`. */
export function isMarineDealerFrom(dealerId, caches) {
  return resolveDealerAssetType(dealerId, caches) === DEALER_ASSET_TYPES.MARINE;
}

/**
 * Is THIS DEALER ROW a marine dealership?
 *
 * The row-based twin of `isMarineDealerFrom`, and it exists because the cached
 * lookup is unusable on the installer side. `companyAPI` / `dealerGroupAPI` hold
 * the INSTALLER's own scope when an installer is signed in — dealerships are not
 * in them — so `isMarineDealerFrom` would resolve every dealership to 'auto' on
 * the walk-in form, silently.
 *
 * Use this wherever a dealer ROW is already in hand (the walk-in form's selected
 * customer, a picker result). `GET /dealers/:id` carries `asset_type`.
 *
 * A row without the field reads as 'auto', same as everywhere else.
 */
export function isMarineDealerRow(row) {
  return normalise(row?.asset_type) === DEALER_ASSET_TYPES.MARINE;
}
